/**
 * The keyboard of a list of rows (contact details, locations, rates, field
 * groups, select options, Settings groups), as pure decisions: what a key
 * means, then what “this row is done” leads to.
 */

/** Where the key was pressed. */
export type RowListKeyTarget =
  /** A text input of a row. */
  | { type: 'text'; empty: boolean }
  /** A picker of a row (label, platform, country, colour): Enter, Space and arrows stay the picker’s. */
  | { type: 'picker' }
  /** The empty row at the end that becomes a row on first input. */
  | { type: 'placeholder'; empty: boolean };

export type RowListKeyPress = {
  key: string;
  /** ⌘ on macOS, Ctrl elsewhere. */
  mod: boolean;
  alt: boolean;
  shift: boolean;
  /** An IME composition is in progress: the key belongs to it. */
  composing: boolean;
  /** The key is held down (auto-repeat). */
  repeat: boolean;
  /** The modifier is Ctrl, where Ctrl+⌫ also deletes a word (Windows, Linux). */
  modIsCtrl: boolean;
  target: RowListKeyTarget;
  /** Every text part of the row is empty. */
  rowBlank: boolean;
  /** The row’s position, so ↑ / ↓ / ⌥↑ / ⌥↓ know where they can go. */
  isFirst: boolean;
  isLast: boolean;
  /** The list keeps the person’s own order (rates, select options, Settings groups). */
  manualOrder: boolean;
};

export type RowListIntent =
  | { type: 'finish-row' }
  | { type: 'remove-row' }
  | { type: 'focus-row'; direction: -1 | 1 }
  | { type: 'move-row'; direction: -1 | 1 }
  /** From the placeholder: Enter while it is empty. */
  | { type: 'list-done' }
  /** From the placeholder: back to the end of the last row. */
  | { type: 'focus-last-row' };

const ARROW_DIRECTION: Partial<Record<string, -1 | 1>> = {
  ArrowUp: -1,
  ArrowDown: 1
};

/** What a key press means in a list of rows; `null` leaves it to the browser and the control. */
export function rowListIntent(press: RowListKeyPress): RowListIntent | null {
  if (press.composing) return null;
  const { key, mod, alt, shift, target } = press;
  // A held ⌫ deletes text; only a fresh press removes a row or leaves the empty row.
  if (key === 'Backspace' && press.repeat) return null;
  const arrow = ARROW_DIRECTION[key] ?? null;

  if (target.type === 'placeholder') {
    if (key === 'Enter' && !mod && !shift && !alt) {
      return target.empty ? { type: 'list-done' } : null;
    }
    if ((key === 'Backspace' && target.empty) || (arrow === -1 && !alt)) {
      return { type: 'focus-last-row' };
    }
    return null;
  }

  if (key === 'Enter' && !mod && !shift && !alt) {
    return target.type === 'picker' ? null : { type: 'finish-row' };
  }
  if (key === 'Backspace' && mod) {
    // Where Ctrl is the modifier, Ctrl+⌫ in text with text in the row deletes a word.
    return press.modIsCtrl && target.type === 'text' && !press.rowBlank
      ? null
      : { type: 'remove-row' };
  }
  if (arrow !== null && alt && !mod && press.manualOrder) {
    const blocked = arrow === -1 ? press.isFirst : press.isLast;
    return blocked ? null : { type: 'move-row', direction: arrow };
  }
  if (target.type !== 'text') return null;
  if (key === 'Backspace' && !mod && !alt && target.empty && press.rowBlank) {
    return { type: 'remove-row' };
  }
  if (arrow !== null && !alt && !mod && !shift) {
    // ↓ from the last row goes on to the placeholder; ↑ from the first stays.
    return arrow === -1 && press.isFirst
      ? null
      : { type: 'focus-row', direction: arrow };
  }
  return null;
}

/** What the row knows about itself when Enter finishes it. */
export type FinishRowFacts = {
  blank: boolean;
  isLast: boolean;
  /** A problem that blocks Save, and the part to move to. */
  error: { part: string } | null;
  /** A hint is shown and this value hasn’t been confirmed with a second Enter yet. */
  unacknowledgedHint: boolean;
  /** Where Enter adds: at the end (lists ordered by id) or right below (own order). */
  insert: 'end' | 'below';
};

export type FinishRowOutcome =
  /** An empty last row: drop it and leave the list (Save, or the next field). */
  | { type: 'drop-and-leave' }
  | { type: 'show-error'; part: string }
  /** Stay; the hint is now acknowledged and the next Enter goes on. */
  | { type: 'stay-on-hint' }
  | { type: 'next-row' }
  | { type: 'placeholder' }
  | { type: 'insert-below' };

export function finishRowOutcome(row: FinishRowFacts): FinishRowOutcome {
  if (row.blank && row.isLast) return { type: 'drop-and-leave' };
  if (row.error !== null) return { type: 'show-error', part: row.error.part };
  if (row.unacknowledgedHint) return { type: 'stay-on-hint' };
  if (row.insert === 'below' && !row.isLast) return { type: 'insert-below' };
  return row.isLast ? { type: 'placeholder' } : { type: 'next-row' };
}
